import { createSelector } from 'reselect';
import { EVENT_TYPES_LOOKUP } from './constants';
import { getEnabledEvents, getEventType } from './utils';

/**
 * getSystemWebhookState
 * Selector for the systemWebhooks state.
 * @param { Object } state: The application state.
 * @returns { Object } The systemWebhooks state.
 */
export const getSystemWebhookState = (state) => state.integrations?.systemWebhooks || {};

/**
 * selectSystemWebhooksList
 * Selects the systemWebhooksList from the systemWebhooks state.
 * @param { Object } state: The application state.
 * @returns { Any } The value of systemWebhooksList from the systemWebhook state.
 */
export const selectSystemWebhooksList = createSelector(getSystemWebhookState, (systemWebhooks) =>
  (systemWebhooks?.systemWebhooksList || []).reduce((systemWebhookMapping, systemWebhook) => {
    const systemWebhookWithEvents = { ...systemWebhook };
    /** Create an object mapping each event type to an initial empty array */
    const eventTypeMappings = Object.keys(EVENT_TYPES_LOOKUP).reduce(
      (mapping, eventType) => ({ ...mapping, [eventType]: [] }),
      {},
    );
    /** Parse through enabled events and sort them into event types */
    const enabledEvents = getEnabledEvents(systemWebhookWithEvents);
    enabledEvents.forEach((event) => {
      const eventType = getEventType(event) || 'others'; // adding fallback if this happens again [FRAM-6244]
      eventTypeMappings[eventType].push(event);
    });
    /** Calculcate enabled events count and add event lists to rowData */
    let eventCount = 0;
    Object.keys(eventTypeMappings).forEach((type) => {
      const eventTypeList = eventTypeMappings[type];
      if (eventTypeList.length > 0) {
        eventCount += eventTypeList.length;
        systemWebhookWithEvents[`${type}Events`] = eventTypeList;
      }
    });
    systemWebhookWithEvents.eventCount = eventCount;
    /** Add filter text */
    systemWebhookWithEvents.filterText = systemWebhook.endpoint;
    return {
      ...systemWebhookMapping,
      [systemWebhookWithEvents.id]: systemWebhookWithEvents,
    };
  }, {}),
);

/**
 * selectSystemWebhooksTableRows
 * Selects the values of the systemWebhooksList object for displaying in the table
 * @param { Object } state: The application state.
 * @returns { Any } The values of systemWebhooksList object
 */
export const selectSystemWebhooksTableRows = createSelector(
  selectSystemWebhooksList,
  (systemWebhooksList) => Object.values(systemWebhooksList),
);

/**
 * selectInitialCallMade
 * Selects the value of initialFetchMade from the systemWebhooks state.
 * @param { Object } state: The application state.
 * @returns { Boolean } Whether an initial fetch has been made
 */
export const selectInitialFetchMade = createSelector(
  getSystemWebhookState,
  (systemWebhooks) => systemWebhooks.initialFetchMade,
);
